import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import type { LucideIcon } from 'lucide-react'
import {
    BarChart3,
    ChevronRight,
    ChevronUp,
    File,
    Image as ImageIcon,
    Link2,
    Music4,
    Package,
    Play,
    Quote,
} from 'lucide-react'
import type { QuoteMediaChip, QuoteMediaKind, QuotePreview } from './quotePreview'
import styles from './quote-line.module.scss'

interface QuoteLineProps {
    authorName?: string | null
    // Flat text form, for callers that have no structured preview.
    snippet?: string | null
    // The structured preview (action tags + media chips + text). When present
    // it supersedes `snippet`. See docs/quote-line-media-and-action-tags.md.
    preview?: QuotePreview | null
    // Label-only lines have no snippet to resolve — suppress the loading
    // placeholder entirely.
    hideSnippet?: boolean
    unavailable?: boolean
    onClick?: (ev: React.SyntheticEvent) => void
    // Which way the press goes, drawn as the pill's trailing chevron: 'up'
    // reveals the quoted message above, in this panel; 'right' opens it in a
    // panel to the right (a title's quote, a target that lives in another
    // thread). Meaningless without onClick.
    jumpDirection?: 'up' | 'right'
    className?: string
    children?: React.ReactNode
}

const KIND_ICONS: Record<QuoteMediaKind, LucideIcon> = {
    image: ImageIcon,
    video: Play,
    audio: Music4,
    file: File,
    link: Link2,
    ordinal: Package,
    poll: BarChart3,
}

// One media placeholder: the small variant when it loads, the kind glyph when
// there is none or it fails (an expired preview URL must never leave a blank
// ringed box).
const MediaChip: React.FC<{ chip: QuoteMediaChip; onFailed?: () => void }> = ({
    chip,
    onFailed,
}) => {
    const [failed, setFailed] = useState(false)
    useEffect(() => setFailed(false), [chip.thumbUrl])

    const Glyph = KIND_ICONS[chip.kind] || ImageIcon
    const showThumb = !!chip.thumbUrl && !failed

    return (
        <span className={styles.mediaChip} title={chip.label} data-quote-media-kind={chip.kind}>
            {showThumb ? (
                <img
                    className={styles.mediaThumb}
                    src={chip.thumbUrl as string}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => {
                        setFailed(true)
                        onFailed?.()
                    }}
                />
            ) : (
                <Glyph size={10} />
            )}
        </span>
    )
}

// The chip run — action tokens, then media placeholders, then the overflow
// count. Shared with the reply modal's quote block so both surfaces render the
// same chips at the same size from the same preview.
export const QuoteChips: React.FC<{
    actionTags: string[]
    media: QuoteMediaChip[]
    overflowCount: number
    // Only the leading chip reports a broken thumbnail: callers that fall back
    // to its label need to know the glyph took over.
    onLeadThumbFailed?: () => void
}> = ({ actionTags, media, overflowCount, onLeadThumbFailed }) => (
    <>
        {actionTags.map((tag, ix) => (
            <span className={styles.actionChip} key={`tag-${ix}-${tag}`}>
                {tag}
            </span>
        ))}
        {media.map((chip, ix) => (
            <MediaChip
                chip={chip}
                key={`media-${ix}-${chip.thumbUrl || chip.kind}`}
                onFailed={ix === 0 ? onLeadThumbFailed : undefined}
            />
        ))}
        {overflowCount > 0 && <span className={styles.overflow}>+{overflowCount}</span>}
    </>
)

// The quote grammar shared by the composer's target chip and the sent-message
// render: a filled quote mark, the target's author in the reply family's
// purple, the post's action tags and media as small placeholders, then a muted
// one-line snippet. A line that goes somewhere (onClick) wears the pill — a
// hairline, a faint wash and a trailing chevron for where it goes — while an
// inert line stays bare. Trailing controls ride in as children.
const QuoteLine: React.FC<QuoteLineProps> = ({
    authorName,
    snippet,
    preview,
    hideSnippet,
    unavailable,
    onClick,
    jumpDirection = 'up',
    className,
    children,
}) => {
    const actionTags = preview?.actionTags || []
    const media = preview?.media || []
    const overflowCount = preview?.overflowCount || 0
    // A thumbnail that fails at load time is only knowable here, so the line —
    // not the pure preview — decides whether the leading chip has to say what
    // it is. Falls back to its label so a broken preview is never a bare glyph.
    const [leadThumbFailed, setLeadThumbFailed] = useState(false)
    useEffect(() => setLeadThumbFailed(false), [media[0]?.thumbUrl])
    const previewText = preview ? preview.text : snippet || ''
    const text = previewText || (leadThumbFailed && media.length ? media[0].label : '')
    // Only a target that resolved to nothing at all is still loading; a photo
    // with no caption is fully resolved and simply has no words.
    const hasChips = actionTags.length > 0 || media.length > 0
    const Jump = jumpDirection === 'right' ? ChevronRight : ChevronUp

    return (
        <span
            className={cn(styles.quoteLine, onClick && styles.clickable, className)}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={
                onClick
                    ? ev => {
                          // Trailing controls handle their own keyboard activation.
                          if (ev.target !== ev.currentTarget) return
                          if (ev.key !== 'Enter' && ev.key !== ' ') return
                          ev.preventDefault()
                          onClick(ev)
                      }
                    : undefined
            }
        >
            <span className={styles.glyph} aria-hidden="true">
                <Quote fill="currentColor" strokeWidth={0} />
            </span>
            {unavailable ? (
                <span className={styles.unavailable}>Original message unavailable</span>
            ) : (
                <>
                    <span className={styles.content}>
                        {authorName && <span className={styles.author}>{authorName}</span>}
                        <QuoteChips
                            actionTags={actionTags}
                            media={media}
                            overflowCount={overflowCount}
                            onLeadThumbFailed={() => setLeadThumbFailed(true)}
                        />
                        {!hideSnippet && (text || !hasChips) && (
                            <span className={styles.snippet}>{text || 'Loading…'}</span>
                        )}
                    </span>
                    {onClick && (
                        <span
                            className={styles.jump}
                            data-quote-jump={jumpDirection}
                            aria-hidden="true"
                        >
                            <Jump size={12} strokeWidth={2.5} />
                        </span>
                    )}
                </>
            )}
            {children}
        </span>
    )
}

export default QuoteLine
